import { Tooltip } from 'bootstrap';
import { useEffect, useRef } from 'react';
import { StandardAlumniRow } from '../WatchlistDashboardService';

/**
 * Shared alumni-row renderer for the Watchlist dashboard. ONE component renders rows from every
 * layer (Signal / Strategic / Activation / logs) because every endpoint returns the same shape.
 * The 3 chips are the engagement signals: alma_mater_pride / marketability / vibrancy. Each is
 * stored as 'HIGH' | 'MODERATE' | 'LOW'; to fit the tight row we show only the first letter
 * (H / M / L) with the full category title + value as a hover tooltip.
 * Colour encodes the VALUE (not the category): High → green, Moderate → blue, Low → red, so the
 * signal reads at a glance. evidence_text is surfaced as a hover tooltip on the name.
 */
// HIGH → success (green), MODERATE → info (blue), LOW → danger (red); unscored → muted.
const toneForValue = (value: string | null): string => {
    switch ((value ?? '').toUpperCase()) {
        case 'HIGH': return 'success';
        case 'MODERATE': return 'info';
        case 'LOW': return 'danger';
        default: return 'secondary';
    }
};

const ScoreChip = ({ label, value }: { label: string; value: string | null }) => {
    const tone = toneForValue(value);
    return (
        <span
            className={`font-10 font-700 bg-${tone} bg-opacity-10 text-${tone} rounded-1 mt-0`}
            style={{
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
            title={value ? `${label}: ${value}` : label}>
            {value ? value.charAt(0).toUpperCase() : '—'}
        </span>
    );
};

const AlumniRow = ({ alumni }: { alumni: StandardAlumniRow }) => {
    const nameRef = useRef<HTMLAnchorElement>(null);
    const programRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const tooltips = [nameRef.current, programRef.current]
            .filter((element): element is HTMLElement => Boolean(element?.getAttribute('data-bs-title')))
            .map((element) => new Tooltip(element, { container: 'body' }));

        return () => tooltips.forEach((tooltip) => tooltip.dispose());
    }, [alumni.evidence_text, alumni.program_and_year]);

    return (
        <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-light">
        {alumni.student_id ? (
            <a
                ref={nameRef}
                href={`/alumni/${alumni.student_id}/profile`}
                target="_blank"
                rel="noopener noreferrer "
                className="font-12 font-600 text-dark-blue text-decoration-none d-inline-block text-truncate"
                data-bs-toggle="tooltip"
                data-bs-title={alumni.evidence_text ?? 'View profile'}>
                {alumni.name ?? '—'}
            </a>
        ) : (
            <span className="font-12 font-600 text-dark" title={alumni.evidence_text ?? ''}>
                {alumni.name ?? '—'}
            </span>
        )}
        <div className="d-flex align-items-center gap-2">
            <span ref={programRef} className="font-11 font-600 text-secondary d-inline-block text-truncate max-150 w-auto text-end" data-bs-toggle="tooltip" data-bs-title={alumni.program_and_year ?? ''}>
                {alumni.program_and_year ?? '—'}
            </span>
            <div className="d-flex gap-1 align-items-center">
                <ScoreChip label="Alma Mater Pride" value={alumni.alma_mater_pride} />
                <ScoreChip label="Marketability" value={alumni.marketability} />
                <ScoreChip label="Vibrancy" value={alumni.vibrancy} />
            </div>
        </div>
        </div>
    );
};

export default AlumniRow;
